// @ts-strict-ignore
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { Service } from 'rainbow-web-sdk/services/service';
import { MD5 } from '..';

export const BOT_SVC = 'BotService';

export class BotData {

    public id: string = null;
    public name: string = null;
    public jid: string = null;
    public avatarId: string = null;
    public avatarUrl: string = null;	// Not filled at creation
    public lastAvatarUpdateDate: string = null;
    public capabilities: string[] = [];
    public isRainbowSupportBot: boolean = false;

    public static createFromData(data: any): BotData {
        const bot = new BotData();

        bot.id = data.id;
        bot.name = data.name;
        bot.jid = data.jid;
        bot.avatarId = data.avatarId || data.id;
        bot.lastAvatarUpdateDate = data.lastAvatarUpdateDate;
        bot.capabilities = data.capabilities; //["SHARING"];
        bot.isRainbowSupportBot = data.isRainbowSupportBot;

        return bot;
    }
}

export abstract class AbstractBotService extends Service {
    public abstract getBots(options: any): Promise<any[]>;
    public abstract getBotById(botId: string): Promise<any>;
}

export class BotService extends AbstractBotService {
    private authService: AuthService = AuthService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();

    private portalURL: string;
    private avatarChangeEventHandler: any = null;

    public botsList: BotData[] = [];

    public static getInstance() {
        return Service.create(BOT_SVC, BotService);
    }

    public static build() {
        return new BotService();
    }

    protected constructor() {
        super();
    }

    /********************************************************/
    /** LIFE CYCLE STUFF                                   */
    /********************************************************/
    public async start(): Promise<void> {
        try {
            this.logger.info(`[botService] === STARTING ===`);

            this.init();
            this.startListeners();

            if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.REPORT_A_PROBLEM)) {
                // Get Rainbow support bot service (Emily) immediately (So it doesn't have to be searched anymore ... normally)
                const supportBot: any = await this.getEmilyBot();
                // Create immediately Emily Bot data and add them in bot arrays
                this.storeEmilyBot(supportBot);
            }

            this.logger.info(`[botService] === STARTED ===`);
        }
        catch (error) {
            this.logger.error(`[botService] === STARTING FAILURE === ${error.message}`);
        }
    }

    public async stop(): Promise<void> {
        this.botsList = [];
        if (this.avatarChangeEventHandler) { this.avatarChangeEventHandler(); this.avatarChangeEventHandler = null; }
        this.logger.info(`[botService] === STOPPED ===`);
    }

    public init(): void {
        // Get and store portalURL
        this.portalURL = config.restServerUrl + "/api/rainbow/enduser/v1.0/";
    }

    /********************************************************/
    /** EVENT HANDLING                                     */
    /********************************************************/
    private startListeners() {
        if (this.avatarChangeEventHandler) { this.avatarChangeEventHandler(); this.avatarChangeEventHandler = null; }
        this.avatarChangeEventHandler = this.eventService.subscribe('ON_COMPANY_SUPPORT_AVATAR_CHANGE_EVENT',
            (_event: any, supportAvatarInfo: any) => this.onSupportAvatarChange(_event, supportAvatarInfo));
    }

    private async onSupportAvatarChange(__event: any, supportAvatarInfo: any) {
        try {
            this.logger.info(`[botService] Emily avatar has been changed`);
            if (supportAvatarInfo.companyId === this.contactService.userContact.company.id) {
                if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.REPORT_A_PROBLEM)) {
                    const supportBot: any = await this.getEmilyBot();
                    this.storeEmilyBot(supportBot);
                }
            }
        }
        catch (error) {
            this.logger.warn("[botService] Impossible to update Emily - " + error.stack || error.message);
        }
    }

    /********************************************************/
    /** COMPANY BOTS                                       */
    /********************************************************/

    /**
     * Get the  list of bot services.
     * Used by SDK (public)
     * Warning when modifying this method
     *
     * @param {object} options [optional] configuration object that may contain the following properties:
     * @property {string} options.format [optional] Allows to retrieve more or less bot services details in response.
     * - small: id, name, jid, capabilities
     * - medium: id, name, jid, isRainbowSupportBot, capabilities
     * - full: id, name, jid, isRainbowSupportBot, capabilities, createdByUserId, avatarId, lastAvatarUpdateDate
     * Default value: full
     * @property {number} options.limit [optional] Allows to specify the number of bot services to retrieve.
     * Default value: 100
     * @property {number} options.offset [optional] Allows to specify the position of first bot to retrieve (first bot if not specified).
     * Warning: if offset > total, no results are returned.
     * @property {string} options.sortField [optional] Sort bots list based on the given field.
     * Default value: name
     * @property {number} options.sortOrder [optional] Allows to specify order when sorting bots list.
     * Default value: 1
     * Allowed values: -1, 1
     * }
     * @returns Promise<any[]>  returns an Object
     * {
     * data :      {array}     // found bots informations with an array
     * limit :     {number}    // limit given
     * offset :    {number}    // offset given
     * total :     {number}    // total returned
     * }
     */
    public async getBots(options: any = null): Promise<any[]> {
        try {
            // Get and Prepare request url query parameters
            const urlOptions: any = {};

            if (options) {
                if (options.format && (typeof options.format === "string") && options.format.length) {
                    urlOptions.format = options.format;
                }
                else {
                    // Default options when the one specified by the client is wrong
                    urlOptions.format = "full";
                }
                if (options.limit) { urlOptions.limit = options.limit; }
                if (options.offset) { urlOptions.offset = options.offset; }
                if (options.sortField) { urlOptions.sortField = options.sortField; }
                if (options.sortOrder && (options.sortOrder === "-1" || options.sortOrder === "1")) {
                    urlOptions.sortOrder = options.sortOrder;
                }
            } else {
                // Default options when no specified by client
                urlOptions.format = "full";
            }

            const urlParams = new URLSearchParams(urlOptions).toString();
            const url = new URL(`${this.portalURL}bots`);
            const headers = this.authService.getRequestHeader();
            url.search = urlParams;

            const responseData = await fetch(url.toString(), { method: 'GET', headers: headers });

            if (!responseData.ok) throw responseData;
            this.logger.info(`[botService] getBots: Get the list of bot services -- success from server`);

            const botData: any = (await responseData.json()).data;

            return (botData);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "getBots");
            this.logger.error(`[botService] getBots failure: ${errorInfo.error.errorDetails} with status ${errorInfo.error.status}`);
            throw errorInfo.error;
        }
    }

    /**
     * Get the data of a bot service from its id
     * Used by SDK (public)
     * Warning when modifying this method
     *
     * @param {string} botId Bot Service unique identifier
     * @returns Promise<any>
     */
    public async getBotById(botId: string): Promise<any> {
        try {
            if (!botId) {
                throw (new Error('Missing botId in the request'));
            }

            // Get BOT information from server
            const url = `${this.portalURL}bots/${botId}`;
            const headers = this.authService.getRequestHeader();

            const responseData = await fetch(url, { method: "GET", headers });
            if (!responseData.ok) throw responseData;
            this.logger.info(`[botService] getBotById success for ${botId}`);

            const botData: any = (await responseData.json()).data;

            return botData;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "getBotById");
            this.logger.error(`[botService] getBotById failure: ${errorInfo.error.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Get the Contact associated to a bot service from its jid
     * The bot service is retrieved first from internal data (botsList) and if not present, a request is sent to server
     * to retrieve all data associated to this bot. This bot is also added in botsList cache.
     *
     * @param jid
     * @param {string} botJid Bot service's jid, should be like 'emily.rainbow.com'.
     * @returns Promise<Contact>
     */
    public async getBotContactByJid(jid: string): Promise<Contact> {
        try {
            if (!jid) {
                throw (new Error('Missing bot jid in the request'));
            }

            // First, search in cache the Bot with the given jid
            let botIndex: number = this.botsList.findIndex((entry) => { return entry.jid === jid; });
            if (botIndex !== -1) {
                // Bot found in cache
                this.logger.info(`[botService] getBotContactByJid - Data for bot jid "${jid}" found in cache`);
                const foundBot: BotData = this.botsList[botIndex];

                // Return the contact associated to the bot.
                // (The contact has been created/updated at the same time as the bot)
                const botContact: Contact = this.contactService.getContactFromCacheByJid(foundBot.jid);

                return botContact;
            }

            // Second, Retrieve Bot from server if not found in cache
            // The only way to do this is to get the list of bot services and find the wanted one
            const allBots: any = await this.getBots();

            botIndex = allBots.findIndex((entry) => { return entry.jid === jid; });
            if (botIndex !== -1) {
                this.logger.info(`[botService] getBotContactByJid - Data for bot jid "${jid}" get from server`);

                // Bot found in data get from server, retrieve its data
                const foundBot: BotData = allBots[botIndex];

                // Add the bot data in cache
                this.botsList.unshift(foundBot);

                // Create/Update the associated contact
                return this.contactService.createBotContact(foundBot.jid, foundBot.name, foundBot.id, foundBot.avatarUrl, foundBot.capabilities, foundBot.isRainbowSupportBot);
            }

            // AT this step, there is a problem, we did not find any information on the Bot with the given jid
            throw (new Error(`No bot with jid ${jid} found in cache or server`));
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "getBotContactByJid");
            this.logger.error(`[botService] ${errorInfo.error.message}`);
            throw errorInfo.error;
        }
    }

    /********************************************************/
    /** COMPANY E-SUPPORT BOT (EMILY)                      */
    /********************************************************/

    /**
     * GET Emily bot data from server
     *
     * @returns {object} Emily bot Object (any)
     */
    private async getEmilyBot(): Promise<any> {
        try {
            // Prepare request url
            const url = this.portalURL + "bots/rainbow-support";
            const headers = this.authService.getRequestHeader();

            const responseData = await fetch(url, { method: "GET", headers });
            if (!responseData.ok) throw responseData;
            this.logger.info(`[botService] Get support bot service (Emily) success from server`);
            const emilyBotData = (await responseData.json()).data;

            // Examples of received data
            // {
            //     "name": "Emily",
            //     "jid": "emily.openrainbow.net",
            //     "createdByUserId": "56e849fc2f3fadaa82cc2aa0",
            //     "createdByLoginEmail": "superadmin@openrainbow.org",
            //     "lastAvatarUpdateDate": "2021-11-04T13:33:03.266Z",
            //     "isRainbowSupportBot": true,
            //     "capabilities": [
            //         "SHARING"
            //     ],
            //     "id": "57e275f578312f02e1d05351",
            //     "avatarId": "6183d79a4fbd0877ef03223d"
            // }

            return (emilyBotData);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "getEmilyBot");
            this.logger.error(`[botService] getEmilyBot failure: ${errorInfo.error.errorDetails} with status ${errorInfo.error.status}`);
            throw errorInfo.error;
        }
    }

    private storeEmilyBot(bot): BotData {
        // Search in cache Emily Bot and if present, replace it with new data retrieved from server
        const emilyIndex: number = this.botsList.findIndex((entry) => { return entry.isRainbowSupportBot === true; }); // Or use name "Emily" for check ?

        if (emilyIndex !== -1) {
            // Remove previous Emily bot data
            this.botsList.splice(emilyIndex, 1);
        }

        // Create again Emily bot data from server and store them in botsList array cache
        const emilyBot: BotData = BotData.createFromData(bot);

        // Save the URL of the avatar
        emilyBot.avatarUrl = this.getBotAvatarUrl(emilyBot.avatarId, emilyBot.lastAvatarUpdateDate);
        this.botsList.unshift(emilyBot);

        // Create or update Emily contact
        this.contactService.createBotContact(emilyBot.jid, emilyBot.name, emilyBot.id, emilyBot.avatarUrl, emilyBot.capabilities, true);
        this.logger.info("[botService] storeEmilyBot - Emily bot data stored in cache and contact updated");

        return emilyBot;
    }

    /**
     * SEARCH Emily data in internal cache (and from server in case of shitty situation)
     *
     * @returns {object} Emily Bot Data Object (BotData)
     */
    public async getEmily(): Promise<BotData> {
        if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.REPORT_A_PROBLEM)) {
            return null;
        }
        // This bot MUST already be in cache, done at service init so no need to request the server
        const foundEmily = this.botsList.filter((bot: BotData) => { return bot.isRainbowSupportBot === true; }); // Or use name "Emily" for check ?

        // Unexpected behaviour, Emily should ALWAYS be present in cache
        if (foundEmily.length === 0) {
            this.logger.error(`[botService] getEmily failure: Unexpected behaviour -- Emily bot not found in cache !`);

            // But just in case ... Trying to solve this problem by getting Emily bot data
            const supportBot: any = await this.getEmilyBot();
            const supportEmilyData: BotData = this.storeEmilyBot(supportBot);
            return supportEmilyData;
        }

        // Return the first found Emily bot ... in fact, there can only be one
        return foundEmily[0];
    }

    /********************************************************/
    /** COMPANY BOT AVATAR STUFF                           */
    /**
     * @param {string} avatarId Identifier of the Bot service's avatar.
     * @param {string} lastAvatarUpdateDate Date of last bot avatar update
     * @returns {Promise} Promise to get the  Avatar (Image format) of the specified Bot
     */
    public getBotAvatarImage(avatarId: string, lastAvatarUpdateDate: string): Promise<any> {
        return new Promise((resolve, reject) => {

            this.logger.info("[botService] getBotAvatarImage");

            // Create the image
            const image = new Image();

            let nbErrors = 0;

            image.onload = () => {
                this.logger.info("[botService] Load bot avatar " + avatarId + " lastAvatarUpdateDate " + lastAvatarUpdateDate + " success");
                resolve(image);
            };

            image.onerror = () => {
                nbErrors++;
                this.logger.warn("[botService] Load bot avatar " + avatarId + " lastAvatarUpdateDate " + lastAvatarUpdateDate + " failure");
                if (nbErrors === 1) {
                    this.logger.warn("[botService] Fallback to default emily avatar");
                    image.src = "/assets/skins/rainbow/images/conversation/icon_headset.png";
                } else {
                    reject(new Error("No image found"));
                }
            };

            // Build & Load the image
            image.src = this.getBotAvatarUrl(avatarId, lastAvatarUpdateDate);
        });
    }

    private getBotAvatarUrl(botId, lastAvatarUpdateDate) {
        // Build the image URL
        const serverURL = config.webservices.protocol + "://" + config.webservices.currentServer;
        let imgSrc = serverURL + "/api/avatar/" + botId + "?size=120";

        if (lastAvatarUpdateDate) imgSrc += "&update=" + MD5(lastAvatarUpdateDate);
        else imgSrc += "&update=" + Date.now();
        return imgSrc;
    }
}
